import React from "react";
import { Car, Home, CheckCircle2 } from "lucide-react";

const HowItWorksSection = () => {
  return (
        <section id="how-it-works" className="py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
                
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">How Peer to Peer EV Charging Works</h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">Simple steps to charge your EV or earn money as a host.</p>
                </div>

                <div className="space-y-16">
                    {/* For Drivers */}
                    <div className="bg-brand-light rounded-[2.5rem] p-8 md:p-12 lg:p-16 flex flex-col lg:flex-row items-center gap-12 border border-gray-100">
                        <div className="flex-1 w-full relative">
                            <div className="aspect-[4/3] bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden flex flex-col p-6">
                                {/* Mockup for app interface */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="h-4 w-1/3 bg-gray-200 rounded-full"></div>
                                    <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                                        <Car className="w-4 h-4 text-brand-green" />
                                    </div>
                                </div>
                                <div className="space-y-4 flex-1">
                                    <div className="h-16 w-full bg-gray-50 rounded-xl border border-gray-100 flex items-center px-4 gap-4">
                                        <div className="w-10 h-10 bg-white shadow-sm rounded-full flex items-center justify-center text-brand-green font-bold">1</div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 w-1/2 bg-gray-200 rounded-full"></div>
                                            <div className="h-2 w-3/4 bg-gray-100 rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="h-16 w-full bg-gray-50 rounded-xl border border-gray-100 flex items-center px-4 gap-4">
                                        <div className="w-10 h-10 bg-white shadow-sm rounded-full flex items-center justify-center text-brand-green font-bold">2</div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 w-1/3 bg-gray-200 rounded-full"></div>
                                            <div className="h-2 w-2/3 bg-gray-100 rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="h-16 w-full bg-brand-green/10 rounded-xl border border-brand-green/20 flex items-center px-4 gap-4">
                                        <div className="w-10 h-10 bg-brand-green shadow-sm shadow-brand-green/30 rounded-full flex items-center justify-center text-white font-bold"><CheckCircle2 className="w-5 h-5" /></div>
                                        <div className="flex-1 space-y-2">
                                            <div className="h-3 w-1/2 bg-brand-green/40 rounded-full"></div>
                                            <div className="h-2 w-3/4 bg-brand-green/20 rounded-full"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 lg:pl-8">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white shadow-sm text-brand-green text-sm font-semibold mb-6">
                                <Car size={16} /> For Drivers
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-6">Charge on the go</h3>
                            
                            <div className="space-y-8">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-brand-green font-bold flex items-center justify-center">1</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">Find & Book</h4>
                                        <p className="text-gray-600 mt-1">Use our map to find a convenient charger near you and book a time that fits your schedule.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-brand-green font-bold flex items-center justify-center">2</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">Arrive & Plug In</h4>
                                        <p className="text-gray-600 mt-1">Follow the host's instructions, park your vehicle, and start charging.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-green text-white font-bold flex items-center justify-center"><CheckCircle2 size={16} /></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">Pay Seamlessly</h4>
                                        <p className="text-gray-600 mt-1">Payment is handled securely and automatically through the app after your session.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* For Hosts */}
                    <div className="bg-brand-light rounded-[2.5rem] p-8 md:p-12 lg:p-16 flex flex-col-reverse lg:flex-row items-center gap-12 border border-gray-100">
                        <div className="flex-1 lg:pr-8">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white shadow-sm text-brand-green text-sm font-semibold mb-6">
                                <Home size={16} /> For Hosts
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-6">Monetize your charger</h3>
                            
                            <div className="space-y-8">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-brand-green font-bold flex items-center justify-center">1</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">List Your Charger</h4>
                                        <p className="text-gray-600 mt-1">Add your charger details, set your availability, and decide your own price.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-brand-green font-bold flex items-center justify-center">2</div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">Accept Bookings</h4>
                                        <p className="text-gray-600 mt-1">Get notified when a driver books your charger. You have full control.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-green text-white font-bold flex items-center justify-center"><CheckCircle2 size={16} /></div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">Get Paid</h4>
                                        <p className="text-gray-600 mt-1">Earn passive income with secure, direct deposits to your bank account.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 w-full relative flex justify-center">
                            <div className="bg-white rounded-[2rem] shadow-xl border border-gray-200 overflow-hidden relative w-[320px] max-w-full">
                                {/* YouTube Shorts style video container */}
                                <div className="relative pt-[177.77%] w-full"> 
                                    <iframe 
                                        className="absolute top-0 left-0 w-full h-full"
                                        src="https://www.youtube-nocookie.com/embed/gCWxZ6w3q9Q" 
                                        title="अपने PeerCharge EV में नया चार्जर कैसे जोड़ें ?" 
                                        frameBorder="0" 
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                        referrerPolicy="strict-origin-when-cross-origin" 
                                        allowFullScreen>
                                    </iframe>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>

  );
};

export default HowItWorksSection;